import { Dispatch } from 'redux';
import ContactsController, {
  ContactsCsvImportJobResponse,
  UpdateContact,
} from '../controllers/ContactsCotroller';
import commonHandler from '../errorHandlers/common-handler';
import contactHandler from '../errorHandlers/contact-handler';
import { snake } from '../helpers/converterString';
import { SelectedContact } from '../interfaces/contacts';
import { MyLeadsAttributes, MyLeadsSearchQuery } from '../interfaces/myLeads';
import { FilterParams, TagsFilters } from '../model/AudiencesModel';
import { ContactsModel } from '../model/ContactsModel';
import modalActions from '../modules/modalsState/actions';
import { IGoogleSheetPickerProps } from '../modules/modalsState/props';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

export const RetrieveByRemoteIdAction = 'RETRIEVE_BY_REMOTE_ID_ACTION';

export type ImportContactsOpts = {
  facebookPageId: number;
  file: File;
  mapping: { [key: string]: string };
  isFirstRowHeader: boolean;
};

class ContactsCRUDActions extends CrudActionContainer {
  setLocalSelectedPageAction: string;
  retrievedHotLeadsAction: string;
  resetAllContactsAction: string;

  constructor() {
    super('CONTACTS');
    this.setLocalSelectedPageAction = this.makeActionKey(
      'SET_SELECTED_CONTACTS_PAGE',
    );
    this.retrievedHotLeadsAction = this.makeActionKey(
      'RETRIEVED_HOT_LEADS_ACTION',
    );
    this.resetAllContactsAction = super.makeActionKey('RESET');
  }

  retrieveAll(
    pageId: number,
    pageNumber?: number,
    pageSize?: number,
    query?: string,
    options?: {
      skipBusy?: boolean;
      withDeliverable?: boolean;
      sortColumn?: string;
      sortDirection?: 'asc' | 'desc';
      audience?: {
        filterParams?: FilterParams;
        tagsFilters?: TagsFilters;
        esFilters?: MyLeadsAttributes;
      };
    },
    mergeResults?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!options?.skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }
      const { esFilters, tagsFilters, filterParams } = options?.audience || {};

      const snakedFilterParams = snake(filterParams);
      const snakedTagsFilters = snake(tagsFilters);

      // snakecasing is turned off for that request in controller, make sure that esFilters come snaked here.
      // Variables && exluded_variables expected to be upper snake case, attributes lower snake case.
      try {
        const response: ContactsModel = await ContactsController.getAll({
          pageId,
          pageNumber,
          pageSize,
          query,
          sortColumn: options?.sortColumn,
          sortDirection: options?.sortDirection,
          filterParams: snakedFilterParams,
          tagsFilters: snakedTagsFilters,
          esFilters,
          withDeliverable: options?.withDeliverable,
        });
        dispatch(
          this.retrievedAll({
            ...response,
            mergeResults,
            hasFilters: !!Object.values(options?.audience || {}).filter(Boolean)
              .length,
          }),
        );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveContactById(
    companyId: number,
    contactId: number,
    pageId: number,
    skipBusy?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }
      try {
        const response: SelectedContact =
          await ContactsController.getContactById(companyId, contactId, pageId);
        dispatch(this.retrieved(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveContactByRemoteId(remoteId: string, pageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: SelectedContact =
          await ContactsController.getContactByRemoteId(remoteId, pageId);
        dispatch(this.retrieved(response));
        return response;
      } catch (error) {
        return contactHandler(dispatch)(error, RetrieveByRemoteIdAction);
      }
    };
  }

  retrieveAllCSV(companyId: number, connectedPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await ContactsController.getAllCSV(
          companyId,
          connectedPageId,
        );
        dispatch(this.retrieved(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  createScheduledExport(
    connectedPageId: number,
    email?: string,
    exportFields?: string[],
    googleSheetProps?: IGoogleSheetPickerProps,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const params = {
          emailAddress: email,
          fieldsForExport: exportFields,
          ...googleSheetProps,
        };

        const response = await ContactsController.createScheduledExport(
          connectedPageId,
          params,
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  getExportableFields(connectedPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        return await ContactsController.getExportableFields(connectedPageId);
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  importContactsFromCsv({
    facebookPageId,
    file,
    mapping,
    isFirstRowHeader,
  }: ImportContactsOpts) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const job = await ContactsController.createContactsCsvImportJob({
          facebookPageId,
          fileName: file.name,
          sourceSize: file.size,
          mapping,
          headersAtFirstLine: isFirstRowHeader,
        });

        if (job.success) {
          const {
            contactsCsv: { uploadUrl, id },
          } = job;
          await ContactsController.uploadContactsCsvFile(uploadUrl, file);
          return await ContactsController.updateContactsCsvImportJob(id, true);
        }

        dispatch(
          modalActions.openPrettyErrorModal({
            errorMessage: job.errors,
            title: 'Error importing contacts',
          }),
        );
        return Promise.reject();
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getContactsCsvImportJob(id: number) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<ContactsCsvImportJobResponse> => {
      dispatch(this.busy());
      try {
        return await ContactsController.getContactsCsvImportJob(id);
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
  setLocalSelectedPage(page: SelectedContact) {
    return (dispatch: Dispatch<StoreState>) =>
      dispatch({
        type: this.setLocalSelectedPageAction,
        payload: page,
      });
  }

  deleteSelectedContact(
    connectedPageId: number,
    contactId: number,
    pageNumber?: number,
    pageSize?: number,
    query?: string,
    eraseRemoteMessages?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response: ContactsModel =
          await ContactsController.deleteSelectedContact(
            connectedPageId,
            contactId,
            pageNumber,
            pageSize,
            query,
            eraseRemoteMessages,
          );

        dispatch(this.retrievedAll(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  contactsBatchDelete(
    fbPageId: number,
    contactsIds: number[],
    filterParams?: MyLeadsSearchQuery,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));

      try {
        const response = await ContactsController.deleteBatchedContacts(
          fbPageId,
          contactsIds,
        );

        if (response && filterParams) {
          setTimeout(
            () =>
              dispatch(
                this.retrieveAll(
                  fbPageId,
                  filterParams.page || 1,
                  undefined,
                  undefined,
                  {
                    audience: {
                      esFilters: filterParams.query,
                      tagsFilters: filterParams.tagsFilters as TagsFilters,
                      filterParams: filterParams.filterParams,
                    },
                    withDeliverable: true,
                    skipBusy: true,
                  },
                ),
              ),
            500,
          );
        }

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveHotLeads(companyId: number, connectedPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const { hotLeads } = await ContactsController.getHotLeads(
          connectedPageId,
        );

        dispatch({ type: this.retrievedHotLeadsAction, hotLeads });
        return hotLeads;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  update(facebookPageId: string, contact: UpdateContact) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await ContactsController.update(facebookPageId, contact);
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  resetAllContacts() {
    return {
      type: this.resetAllContactsAction,
    };
  }

  getContactsMostVisistedUrls(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>): Promise<string[]> => {
      try {
        return await ContactsController.getContactsMostVisistedUrls(
          facebookPageId,
        );
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }
}

const contactsCRUDActions = new ContactsCRUDActions();

export default contactsCRUDActions;

export const useContactsCRUDActions = getActionHookBuilder(
  contactsCRUDActions,
  [
    'update',
    'retrieveContactById',
    'retrieveContactByRemoteId',
    'retrieveAll',
    'deleteSelectedContact',
    'getContactsCsvImportJob',
    'resetAllContacts',
    'createScheduledExport',
    'getExportableFields',
    'getContactsMostVisistedUrls',
    'contactsBatchDelete',
  ],
);
